import { Separator } from "~/react/ui/separator"
import { useState } from "react"
import { Input } from "~/react/ui/input"
import { Radio } from "~/react/ui/radio"
import { Select } from "~/react/ui/select"
import { campaigns_path } from "~/lib/routes"
interface Campaign {
  id: number | null
  name: string
}

// Triggers creation of new campaign using the campaigns API
// Returns the response from the API
// params:
// - name: string
// - source: string
//   - "domain_search" for domain search
//   - "leads_list" for leads list
//   - "leads_actions" for leads actions
//   - "leads_list_actions" for leads list actions
//   - "leads_list_actions" for leads list actions
export const createCampaign = (
  name: string,
  source: string,
): Promise<Response> => {
  return fetch(campaigns_path(), {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")
        ?.content,
    },
    body: JSON.stringify({
      name,
      action_source: source,
    }),
  })
}

// Triggers addition of recipients to a campaign using the campaigns API
// Returns the response from the API
// params:
// - campaignId: number
// - emails: string[]
export const addRecipientsToCampaign = (
  campaignId: number,
  emails: string[],
): Promise<Response> => {
  return fetch(App.api.addToCampaign(campaignId), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ emails }),
  })
}

interface LeadCampaignsManagerProps {
  leadId: number
  campaigns: Campaign[]
  campaignToUse: Campaign | null
  setCampaignToUse: (campaign: Campaign | null) => void
}
export const LeadCampaignsManager = ({
  leadId,
  campaigns,
  campaignToUse,
  setCampaignToUse,
}: LeadCampaignsManagerProps) => {
  const [campaignType, setCampaignType] = useState<"existing" | "new">(
    campaigns.length === 0 ? "new" : "existing",
  )
  const [campaignName, setCampaignName] = useState("")
  const [campaignNameError, setCampaignNameError] = useState(false)
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(
    campaigns.length > 0 ? campaigns[0] : null,
  )

  return (
    <>
      {campaigns.length === 0 ? (
        <>
          <div className="empty-space empty-space--sm p-0 text-start">
            <strong className="empty-space__title">
              You don't have any sequence yet.
            </strong>
            <p className="empty-space__desc">
              Create your first sequence and start reaching people that matters
              to your business.
            </p>
          </div>
          <Separator className="my-4" />
        </>
      ) : (
        <>
          <div className="h-form-group mb-4">
            <div className="h-form-group__input">
              <Radio
                id={`lead-${leadId}-campaigns-manager-existing-campaigns`}
                name={`lead-${leadId}-campaigns-manager-campaign-type`}
                checked={campaignType === "existing"}
                onChange={() => {
                  setCampaignType("existing")
                  setCampaignName("")
                  setCampaignNameError(false)
                  setSelectedCampaign(campaigns[0])
                  setCampaignToUse(campaigns[0])
                }}
              />
            </div>
            <div className="h-form-group__infos">
              <label
                className="h-label h-label--sm"
                htmlFor={`lead-${leadId}-campaigns-manager-existing-campaigns`}
              >
                Add to existing sequence
              </label>
              <label
                className="sr-only"
                htmlFor={`lead-${leadId}-campaigns-manager-campaigns`}
              >
                Select sequence
              </label>
              <Select
                id={`lead-${leadId}-campaigns-manager-campaigns`}
                name={`lead-${leadId}-campaigns-manager-campaigns`}
                className="h-selectize--block"
                isDisabled={campaignType === "new"}
                onChange={(e) => {
                  setSelectedCampaign(
                    campaigns.find((campaign) => campaign.id === e.value)!,
                  )
                  setCampaignToUse(
                    campaigns.find((campaign) => campaign.id === e.value)!,
                  )
                }}
                defaultValue={selectedCampaign?.id}
                options={campaigns.map((campaign) => ({
                  value: campaign.id,
                  label: campaign.name,
                }))}
              />
            </div>
          </div>
        </>
      )}

      <div className="h-form-group">
        <div className="h-form-group__input">
          <Radio
            id={`lead-${leadId}-campaigns-manager-new-campaign`}
            name={`lead-${leadId}-campaigns-manager-campaign-type`}
            checked={campaignType === "new"}
            onChange={() => {
              setCampaignType("new")
              setSelectedCampaign(null)
            }}
          />
        </div>
        <div className="h-form-group__infos">
          <label
            className="h-label h-label--sm"
            htmlFor={`lead-${leadId}-campaigns-manager-new-campaign`}
          >
            Create a new sequence
          </label>
          <Input
            size="sm"
            fullWidth
            disabled={campaignType === "existing"}
            value={campaignName}
            onChange={(e) => {
              setCampaignName(e.target.value)
              setCampaignNameError(false)
              setSelectedCampaign({ id: null, name: e.target.value })
              setCampaignToUse({ id: null, name: e.target.value })
            }}
          />
          {campaignNameError && (
            <div className="h-hint h-hint--error mt-2">
              <span
                className="far fa-circle-exclamation"
                aria-hidden="true"
              ></span>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
